'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { Check, Truck, CreditCard, ClipboardCheck } from 'lucide-react';

export type CheckoutStep = 'shipping' | 'payment' | 'review';

interface StepConfig {
  id: CheckoutStep;
  label: string;
  icon: React.ReactNode;
}

const STEPS: StepConfig[] = [
  { id: 'shipping', label: 'Shipping', icon: <Truck className="w-5 h-5" /> },
  { id: 'payment', label: 'Payment', icon: <CreditCard className="w-5 h-5" /> },
  { id: 'review', label: 'Review', icon: <ClipboardCheck className="w-5 h-5" /> },
];

interface CheckoutStepperProps {
  currentStep: CheckoutStep;
  onStepClick?: (step: CheckoutStep) => void;
  completedSteps?: CheckoutStep[];
}

export function CheckoutStepper({
  currentStep,
  onStepClick,
  completedSteps = []
}: CheckoutStepperProps) {
  const currentIndex = STEPS.findIndex(s => s.id === currentStep);

  const isStepCompleted = (stepId: CheckoutStep) => completedSteps.includes(stepId);

  const isStepAccessible = (stepId: CheckoutStep) => {
    const stepIndex = STEPS.findIndex(s => s.id === stepId);
    return stepIndex <= currentIndex || isStepCompleted(stepId);
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-between relative">
        {STEPS.map((step, index) => {
          const isActive = step.id === currentStep;
          const isCompleted = isStepCompleted(step.id);
          const isAccessible = isStepAccessible(step.id);

          return (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center relative">
                <motion.button
                  type="button"
                  onClick={() => isAccessible && onStepClick?.(step.id)}
                  disabled={!isAccessible}
                  className={`
                    relative flex items-center justify-center w-12 h-12 rounded-full 
                    border-2 transition-all duration-300
                    ${isActive
                      ? 'border-hsecondary-500 bg-hsecondary-500 text-white shadow-lg'
                      : isCompleted
                        ? 'border-hsecondary-500 bg-hsecondary-500 text-white'
                        : isAccessible
                          ? 'border-gray-200 bg-white text-hsecondary-500 hover:border-hsecondary-500/50'
                          : 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                    }
                  `}
                  whileHover={isAccessible ? { scale: 1.05 } : {}}
                  whileTap={isAccessible ? { scale: 0.95 } : {}}
                  initial={false}
                  animate={{
                    scale: isActive ? 1.1 : 1,
                  }}
                >
                  {isCompleted ? (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    >
                      <Check className="w-5 h-5" />
                    </motion.div>
                  ) : (
                    step.icon
                  )}

                  {isActive && (
                    <motion.div
                      layoutId="activeStepRing"
                      className="absolute inset-0 rounded-full border-2 border-hsecondary-500/30"
                      initial={false}
                      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                      style={{ margin: -4 }}
                    />
                  )}
                </motion.button>

                <motion.span
                  className={`
                    mt-2 text-xs font-medium transition-colors duration-300
                    ${isActive
                      ? 'text-hsecondary-500'
                      : isCompleted
                        ? 'text-hsecondary-500'
                        : 'text-gray-500'
                    }
                  `}
                  animate={{ opacity: 1 }}
                >
                  {step.label}
                </motion.span>
              </div>

              {index < STEPS.length - 1 && (
                <div className="flex-1 mx-2 sm:mx-4 relative">
                  <div className="h-0.5 bg-gray-100 rounded-full w-full">
                    <motion.div
                      className="h-full bg-hsecondary-500 rounded-full"
                      initial={{ width: '0%' }}
                      animate={{
                        width: index < currentIndex || isCompleted ? '100%' : '0%'
                      }}
                      transition={{ duration: 0.5, ease: 'easeInOut' }}
                    />
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

export { STEPS };
